import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Gridboard, Service } from 'webapp-types';
import { uniqueId } from 'webapp/utils/common';

import type Block from 'webapp/models-custom/block';
import Serviced from 'webapp/models-custom/serviced';

export interface GridNode {
	id: string | number;
	x: number;
	y: number;
	width: number;
	height: number;
	el?: JQuery;
}

export interface GridPosition {
	x: number;
	y: number;
	w: number;
	h: number;
}

export type GridSize = Pick<GridPosition, 'w' | 'h'>;

export default class DatawallGrid extends Serviced {
	@service('flow/genie-board-build')
	declare genieBoardBuildService: Service.Flow.GenieBoardBuild;

	wallWidth;
	wallHeight;
	maxMoveIterations; // how many blocks (including the given block) we may move on collision
	minimumSize; // width and/or height

	@tracked grid: Gridboard.Instance | null = null;
	@tracked gridEngine: Gridboard.Engine | null = null;
	@tracked placeholdersGrid: Gridboard.Instance | null = null;

	constructor() {
		super();
		this.wallWidth = DatawallGrid.wallWidth;
		this.wallHeight = DatawallGrid.wallHeight;
		this.maxMoveIterations = DatawallGrid.maxMoveIterations;
		this.minimumSize = DatawallGrid.minimumSize;
	}

	findFreeSpace(w = this.minimumSize, h = this.minimumSize): GridPosition | null {
		const gridEngine = this.gridEngine;
		if (gridEngine) {
			return gridEngine.findFreeSpace(w, h);
		} else {
			return null;
		}
	}

	findSpaceWithFallbackResize(
		x: number,
		y: number,
		w: number,
		h: number,
	): { freeSpace: GridPosition; blocksResized: GridNode[] } {
		const blocksResized: GridNode[] = [];
		let freeSpace: GridPosition | null = null;

		if (this.gridEngine?.isAreaEmpty(x, y, w, h)) {
			// have free right here, success
			freeSpace = { x, y, w, h };
		} else {
			// check if have free of that size somewhere else
			freeSpace = this.findFreeSpace(w, h);
			if (!freeSpace) {
				// is there free space for minimal block ?
				freeSpace = this.findFreeSpace();

				if (!freeSpace) {
					// must resize some other block
					const resizedNode = this.makeFreeSpace();
					if (resizedNode) {
						blocksResized.push(resizedNode);
						freeSpace = this.findFreeSpace();
					}
				}
			}
		}

		return { freeSpace: freeSpace!, blocksResized };
	}

	makeFreeSpace(): GridNode | null {
		// resize the biggest node to fit the minimal size block
		let biggestNode: GridNode | undefined;

		const nodes = this.gridEngine?.nodes.map((n: GridNode) => Object.assign({}, n));
		const engine = new window.GridBoard.Engine(
			{
				width: this.wallWidth,
				height: this.wallHeight,
			},
			nodes,
			() => {
				/* nothing */
			},
		);

		// find the biggest node
		engine.nodes.forEach((node: GridNode) => {
			if (!biggestNode || node.width * node.height > biggestNode.width * biggestNode.height) {
				biggestNode = node;
			}
		});

		if (biggestNode) {
			const hasFreeSpace = () => {
				return !!engine.findFreeSpace(this.minimumSize, this.minimumSize);
			};

			// first find the prop that we'll be reducing
			let prop: keyof GridNode | '' = '';
			if (biggestNode.width > biggestNode.height) {
				prop = 'width';
			} else if (biggestNode.height > this.minimumSize) {
				prop = 'height';
			}

			// go through max two rounds of reducing (bcos min size is 2)
			if (prop) {
				biggestNode[prop] -= 1;

				if (!hasFreeSpace()) {
					if (biggestNode[prop] > this.minimumSize) {
						biggestNode[prop] -= 1;

						if (!hasFreeSpace()) {
							biggestNode = undefined;
						}
					} else {
						biggestNode = undefined;
					}
				}
			} else {
				biggestNode = undefined; // we didn't resize anything
			}
		}

		let node: GridNode | null = null;
		// if we made changes, also update the node size in the current grid
		if (biggestNode) {
			node =
				this.gridEngine?.nodes.find((n: GridNode) => {
					return n.id === biggestNode?.id;
				}) ?? null;
			if (node) {
				node.width = biggestNode.width;
				node.height = biggestNode.height;
			}
		}

		return node;
	}

	initMainGrid(el: JQuery, opts: Gridboard.Options, placeholdersEl?: JQuery): void {
		const mainGrid = this.initGrid(el, opts),
			minSize = this.minimumSize;

		this.grid = mainGrid;
		this.gridEngine = mainGrid.grid;

		if (placeholdersEl?.length) {
			this.initPlaceholdersGrid(placeholdersEl);

			mainGrid.grid.onNodePositionChange = () => {
				next(this, this.updatePlaceholdersGrid);
			};

			mainGrid.getAdjustedNodeProps = (posX: number, posY: number) => {
				// this is used only when dragging from external source (dragging a new block)
				// we try to get the pos / size from placeholders under

				let x = Math.max(0, posX);
				let y = Math.max(0, posY);
				let width = minSize;
				let height = minSize;

				// first check if we have a placeholder under
				const placeholdersGrid = this.placeholdersGrid!;
				const plNode = placeholdersGrid.grid.whatIsHere(x, y)[0];

				if (plNode) {
					x = plNode.x;
					y = plNode.y;
					width = plNode.width;
					height = plNode.height;
				} else {
					// placeholder node is removed when drag placeholder is added
					// so if there is a drag placeholder here, use size of that one
					const cNodes = mainGrid.grid.whatIsHere(x, y, width, height),
						placeholderNode = cNodes.find(
							(n: GridNode) => n.el === mainGrid.placeholder,
						);
					if (placeholderNode) {
						x = placeholderNode.x;
						y = placeholderNode.y;
						width = placeholderNode.width;
						height = placeholderNode.height;
					}
				}

				if (
					width < minSize ||
					height < minSize ||
					!placeholdersGrid.grid.isPositionValid(x, y, width, height)
				) {
					// if invalid, return to initial positions
					// can happen f.ex. bcos of tiny placeholders
					x = posX;
					y = posY;
					width = minSize;
					height = minSize;
				}

				return { x, y, width, height };
			};
		}

		mainGrid.placeholder = $(
			`<div class="gridboard-placeholder gridboard-item" data-gb-min-width="${minSize}" data-gb-min-height="${minSize}"><div class="placeholder-content drag-placeholder block"><div class="dashed-border"><ui-icon name="DragAndDropArea" size="2xl" class="bounce"></ui-icon><div>Drop here</div></div></div></div>`,
		);
	}

	private initPlaceholdersGrid(el: JQuery): void {
		const opts: Gridboard.Options = {
			staticGrid: true,
			maxMoveIterations: 1,
			allowFallbackResize: false,
			resizable: false,
			draggable: false,
		};

		const placeholdersGrid = this.initGrid(el, opts);

		this.placeholdersGrid = placeholdersGrid;

		this.updatePlaceholdersGrid();
	}

	private initGrid(el: JQuery, opts: Gridboard.Options): Gridboard.Instance {
		const defaultOpts = {
			width: this.wallWidth,
			height: this.wallHeight,
			maxMoveIterations: this.maxMoveIterations,
			allowFallbackResize: true,
			minWidth: 0, // don't use one-column mode
			acceptDraggables: '.designer-panel--left li.datablock',
			animate: false,
		};

		opts = Object.assign({}, defaultOpts, opts);

		el.gridboard(opts);

		if (!opts.staticGrid) {
			if (opts.onChange) {
				el.on(
					'change',
					opts.onChange ||
						(() => {
							/*do nothing*/
						}),
				);
			}

			if (opts.onDrop) {
				el.on('dropped', opts.onDrop);
			}
		}

		return el.data('gridboard') as Gridboard.Instance;
	}

	initGridWithoutDOM(blocks: Block[], changeFn?: (changes: GridNode[]) => void): void {
		const nodes = (blocks || []).map((block) => {
			return {
				id: String(block.localId),
				x: block.posX,
				y: block.posY,
				width: block.width,
				height: block.height,
			};
		});

		const engine = new window.GridBoard.Engine(
			{
				width: this.wallWidth,
				height: this.wallHeight,
				maxMoveIterations: this.maxMoveIterations,
				allowFallbackResize: true,
			},
			nodes,
			changeFn,
		);

		this.gridEngine = engine;
	}

	private updatePlaceholdersGrid(): void {
		const placeholdersGrid = this.placeholdersGrid,
			mainGridEngine = this.gridEngine;

		if (!(mainGridEngine && placeholdersGrid)) {
			return;
		}

		placeholdersGrid.grid.nodes.forEach((node: GridNode) => {
			// remove all that are covered on main grid
			if (!mainGridEngine.isAreaEmpty(node.x, node.y, node.width, node.height)) {
				placeholdersGrid.removeElement(node.el);
			}
		});

		// work with cloned engine so we don't have to DOM too much
		const clonedGrid = placeholdersGrid.grid.cloneGrid();

		// remove ALL nodes so we can put optimal placeholders on
		clonedGrid.nodes = [];

		// add dummy nodes to placeholder grid where the real blocks are
		mainGridEngine.nodes.forEach((node: GridNode) => {
			clonedGrid.addNode({
				x: node.x,
				y: node.y,
				width: node.width,
				height: node.height,
				id: 'dummy',
			});
		});

		// as close to default as possible
		DatawallGrid.specialPlaceholderPositions.forEach((pos) => {
			const node: GridNode = {
				x: pos[0]!,
				y: pos[1]!,
				width: pos[2]!,
				height: pos[3]!,
				id: uniqueId(),
			};
			this.addOptimizedPlaceholderNode(clonedGrid, node);
		});

		this.fillGridWithSize(clonedGrid, this.minimumSize, this.minimumSize);
		this.fillGridWithSize(clonedGrid, 1, 1);

		// remove the nodes that exist on the main grid
		clonedGrid.nodes
			.filter((item: GridNode) => item.id === 'dummy')
			.forEach((node: GridNode) => {
				clonedGrid.removeNode(node);
			});

		// now compare original with cloned and make necessary changes
		clonedGrid.nodes.forEach((node: GridNode) => {
			const nodesHere = placeholdersGrid.grid.whatIsHere(
				node.x,
				node.y,
				node.width,
				node.height,
			);
			let isCorrectAdded = false;

			// if node like that already exists, leave it
			// otherwise, remove it and add the correct one
			nodesHere.forEach((n: GridNode) => {
				if (
					n.x === node.x &&
					n.y === node.y &&
					n.width === node.width &&
					n.height === node.height
				) {
					isCorrectAdded = true;
				} else {
					placeholdersGrid.removeElement(n.el);
				}
			});

			if (!isCorrectAdded) {
				const populateWithAINode = `
					<div class="populate-with-ai">
						or <ui-icon name="Ai" size="sm"></ui-icon><u>Populate with AI</u>
					</div>`;

				const elNode = `
					<div>
						<div class="gridboard-item-content">
							<div class="add-metric"><span>Add metric</span></div>
							${this.genieBoardBuildService.isAvailable ? populateWithAINode : ''}
						</div>
					</div>`;

				const el = $(elNode);
				placeholdersGrid.addElement(el, node.x, node.y, node.width, node.height);
			}
		});
	}

	private addOptimizedPlaceholderNode(grid: Gridboard.Engine, node: GridNode) {
		const minSize = this.minimumSize;

		// try to resize the node in any or all directions to cover any possible 1x1 spacings

		if (node.width < minSize || node.height < minSize) {
			if (grid.canAddNode(node)) {
				grid.addNode(node);
				return true;
			}
			return false;
		}

		// if does not fit, first try shrinking (once)
		if (!grid.canAddNode(node)) {
			const handleFit = (shrunkNode: GridNode) => {
				if (
					shrunkNode.width >= minSize &&
					shrunkNode.height >= minSize &&
					grid.canAddNode(shrunkNode)
				) {
					node = shrunkNode;
					return true;
				}
				return false;
			};

			// -1 right
			let tmp: GridNode = Object.assign({}, node, { width: node.width - 1 });
			if (!handleFit(tmp)) {
				// -1 left
				tmp = Object.assign({}, node, { x: node.x + 1, width: node.width - 1 });
				if (!handleFit(tmp)) {
					// -1 down
					tmp = Object.assign({}, node, { height: node.height - 1 });
					if (!handleFit(tmp)) {
						// -1 up
						tmp = Object.assign({}, node, { y: node.y + 1, height: node.height - 1 });
						handleFit(tmp);
					}
				}
			}
		}

		// then try enlarge (multiple directions)
		if (grid.canAddNode(node)) {
			const handleFit = (node1: GridNode, node2: GridNode) => {
				if (grid.canAddNode(node1) && !grid.canAddNode(node2)) {
					node = node1;
					return true;
				}
				return false;
			};

			// +1 right
			let node1: GridNode = Object.assign({}, node, { width: node.width + 1 });
			let node2: GridNode = Object.assign({}, node, { width: node.width + 2 });
			handleFit(node1, node2);

			// +1 left
			node1 = Object.assign({}, node, { x: node.x - 1, width: node.width + 1 });
			node2 = Object.assign({}, node, { x: node.x - 2, width: node.width + 2 });
			handleFit(node1, node2);

			// +1 down
			node1 = Object.assign({}, node, { height: node.height + 1 });
			node2 = Object.assign({}, node, { height: node.height + 2 });
			handleFit(node1, node2);

			// +1 up
			node1 = Object.assign({}, node, { y: node.y - 1, height: node.height + 1 });
			node2 = Object.assign({}, node, { y: node.y - 2, height: node.height + 2 });
			handleFit(node1, node2);

			// add the node - it's either the modified (stretched) or the original
			grid.addNode(node);
			return true;
		}

		return false;
	}

	private fillGridWithSize(grid: Gridboard.Engine, width: number, height: number) {
		for (let x = 0; x < this.wallWidth; x++) {
			for (let y = 0; y < this.wallHeight; y++) {
				const node: GridNode = { x, y, width, height, id: uniqueId() };
				this.addOptimizedPlaceholderNode(grid, node);
			}
		}
	}

	updateBlockPosition(nodeId: string, size: GridPosition): void {
		const node = this.gridEngine?.getNodeById(nodeId);
		if (node) {
			this.gridEngine?.setNewPosition(node, size.x, size.y, size.w, size.h);
			if (node._grid) {
				node._grid.setElAttributes(node.el, {
					x: size.x,
					y: size.y,
					width: size.w,
					height: size.h,
				});
			}
		}
	}

	updateBlockMoveAndResize(nodeId: string, noMove = false, noResize = false): void {
		const node = this.gridEngine?.getNodeById(nodeId);
		if (node) {
			node.noMove = noMove;
			node.noResize = noResize;
		}
	}

	fixOverlappingPositions(): void {
		this.gridEngine?.fixOverlappingPositions(10, true);
	}

	addNodeWithoutDOM(nodeId: string, size: GridPosition): void {
		this.gridEngine?.addNode({
			id: nodeId,
			x: size.x,
			y: size.y,
			width: size.w,
			height: size.h,
		});
	}

	triggerContainerResizedEvent(): void {
		// to update block sizes / styles
		if (this.grid) {
			this.grid.container.trigger('resize.gridboard');
		}
		if (this.placeholdersGrid) {
			this.placeholdersGrid.container.trigger('resize.gridboard');
		}
	}

	addElement(el: JQuery): void {
		if (this.grid) {
			this.grid.addElement(el);
		}
	}

	removeElement(el: JQuery): void {
		if (this.grid) {
			this.grid.removeElement(el);
		}
	}

	onExternalDragStart(): void {
		if (this.grid) {
			this.grid.placeholder.addClass('drag-external');
		}
	}

	onExternalDragEnd(): void {
		if (this.grid) {
			this.grid.placeholder.removeClass('drag-external');
		}
	}

	destroyGrid(): void {
		const grid = this.grid,
			placeholdersGrid = this.placeholdersGrid;

		if (grid) {
			grid.container.off('change dropped');
			grid.destroy(false);
			this.grid = null;
			this.gridEngine = null;
		}

		if (placeholdersGrid) {
			placeholdersGrid.destroy(false);
			this.placeholdersGrid = null;
		}
	}

	static specialPlaceholderPositions = [
		[0, 0, 6, 8],
		[6, 0, 10, 4],
		[6, 4, 5, 4],
		[11, 4, 5, 2],
		[11, 6, 5, 2],
	];

	static getPredefinedBlockSizes(): Record<number, GridSize[]> {
		// this is only used if all blocks on board have invalid sizes

		const sizes: Record<number, GridSize[]> = {};

		let size: GridSize;

		// 1 block
		sizes[1] = [{ w: 16, h: 8 }];

		// 2 blocks
		size = { w: 8, h: 8 };
		sizes[2] = [size, size];

		// 3 blocks
		size = { w: 6, h: 8 };
		sizes[3] = [size, size, { w: 4, h: 8 }];

		// 4 blocks
		size = { w: 8, h: 4 };
		sizes[4] = [size, size, size, size];

		// 5 blocks
		size = { w: 6, h: 4 };
		sizes[5] = [size, size, size, size, { w: 4, h: 8 }];

		// 6 blocks
		size = { w: 6, h: 4 };
		sizes[6] = [size, size, size, size];
		size = { w: 4, h: 4 };
		sizes[6].push(size, size);

		// 7 blocks
		size = { w: 4, h: 4 };
		sizes[7] = [size, size, size, size, size, size, { w: 4, h: 8 }];

		// 8 blocks
		size = { w: 4, h: 4 };
		sizes[8] = [size, size, size, size, size, size, size, size];

		// 9 blocks
		size = { w: 4, h: 4 };
		sizes[9] = [size, size, size, size, size, size, size];
		size = { w: 4, h: 2 };
		sizes[9].push(size, size);

		// 10 blocks
		size = { w: 4, h: 4 };
		sizes[10] = [size, size, size, size, size, size];
		size = { w: 4, h: 2 };
		sizes[10].push(size, size, size, size);

		// 11 blocks
		size = { w: 4, h: 4 };
		sizes[11] = [size, size, size, size, size];
		size = { w: 4, h: 2 };
		sizes[11].push(size, size, size, size, size, size);

		// 12 blocks
		size = { w: 4, h: 4 };
		sizes[12] = [size, size, size, size];
		size = { w: 4, h: 2 };
		sizes[12].push(size, size, size, size, size, size, size, size);

		// 13 blocks
		size = { w: 4, h: 4 };
		sizes[13] = [size, size, size];
		size = { w: 4, h: 2 };
		sizes[13].push(size, size, size, size, size, size, size, size, size, size);

		// 14 blocks
		size = { w: 4, h: 4 };
		sizes[14] = [size, size];
		size = { w: 4, h: 2 };
		sizes[14].push(size, size, size, size, size, size, size, size, size, size, size, size);

		// 15 blocks
		size = { w: 4, h: 4 };
		sizes[15] = [size];
		size = { w: 4, h: 2 };
		sizes[15].push(
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
			size,
		);

		// 16+ blocks: 4x2 as many as we can
		size = { w: 4, h: 2 };
		for (let i = 16; i <= 32; i++) {
			const iSizes: GridSize[] = [];
			for (let j = 32 - i; j > 0; j--) {
				iSizes.push(size);
			}
			sizes[i] = iSizes;
		}

		// fill the empties with minimum size
		for (let i = 1; i <= 32; i++) {
			const iSizes = sizes[i]!, // 1-32 are all set above
				predefinedLength = iSizes.length;
			for (let j = 0; j < i - predefinedLength; j++) {
				iSizes.push({ w: this.minimumSize, h: this.minimumSize });
			}
		}

		return sizes;
	}

	static calculateReportBlockWidthFromSlideWidth(
		containerWidth: number,
		blockWidth: number,
	): number {
		const cellWidth = containerWidth / this.wallWidth;
		const calculatedWidth = Math.ceil(blockWidth / cellWidth);
		const maxWidth = Math.floor(this.wallWidth / 2);
		return Math.min(this.wallWidth, maxWidth, calculatedWidth);
	}

	static calculateReportBlockHeightFromSlideHeight(
		containerHeight: number,
		blockHeight: number,
	): number {
		const cellHeight = containerHeight / this.wallHeight;
		const calculatedHeight = Math.ceil(blockHeight / cellHeight);
		const maxHeight = Math.floor(this.wallHeight / 2);
		return Math.min(this.wallHeight, maxHeight, calculatedHeight);
	}

	static wallWidth = 16;
	static wallHeight = 8;
	static maxMoveIterations = 6; // how many blocks (including the given block) we may move on collision
	static minimumSize = 2; // width and/or height
}
